import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixSelectListOption } from "@app/shared/molecules/cerrix-select-list/cerrix-select-list.component";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixAvatarGroupComponent } from "@app/shared/molecules/cerrix-avatar-group/cerrix-avatar-group.component";
import {
    CerrixCardToggleGroupComponent,
    CerrixCardToggleOption,
} from "@app/shared/molecules/cerrix-card-toggle-group/cerrix-card-toggle-group.component";
import { CerrixUserPickerComponent } from "@app/shared/organisms/cerrix-user-picker/cerrix-user-picker.component";
import { CerrixUserTypePickerConfig } from "./models/cerrix-user-type-picker-config";

/**
 * A field that lets the user pick users for several caller-configured types (owner, responsible,
 * informed, ...) at once, one outer tab per type. Each type independently controls its own
 * selectable users, single- vs multi-select behaviour. With a single type there is nothing to
 * switch between, so the type box is left out and only the picker shows.
 */
@Component({
    selector: "app-cerrix-user-type-picker",
    templateUrl: "./cerrix-user-type-picker.component.html",
    styleUrls: ["./cerrix-user-type-picker.component.scss"],
    imports: [
        CerrixLabelComponent,
        CerrixInstructionsIconComponent,
        CerrixAvatarGroupComponent,
        CerrixCardToggleGroupComponent,
        CerrixUserPickerComponent,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CerrixUserTypePickerComponent implements OnChanges {
    @Input() name = "user-type-picker";
    @Input() types: CerrixUserTypePickerConfig[] = [];
    @Input() value: Record<string, string[]> = {};
    @Input() readonly: boolean = false;

    @Output() valueChange: EventEmitter<Record<string, string[]>> = new EventEmitter<
        Record<string, string[]>
    >();

    @Input() label: string;
    @Input() infoText: string = "";

    // Mirrors `value`, but always has every configured type's key so template reads never need `?? []`.
    protected selectedValues: Record<string, string[]> = {};

    // The type whose colleagues/selected split is currently shown below the selector row.
    protected activeTypeIndex = 0;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["value"] || changes["types"]) {
            this.selectedValues = Object.fromEntries(
                this.types.map((type) => [type.key, this.value?.[type.key] ?? []]),
            );
        }

        // Reset on `types` only: resetting on `value` would bounce the user back to the
        // first box every time a pick round-trips through the parent's two-way binding.
        if (changes["types"]) {
            this.activeTypeIndex = 0;
        }
    }

    protected get activeType(): CerrixUserTypePickerConfig | undefined {
        return this.types[this.activeTypeIndex];
    }

    // Options for app-cerrix-card-toggle-group; `data` carries the original type back to the
    // caller-facing selectedNames()/selectType() calls made from the projected `cardContent`.
    protected get cardOptions(): CerrixCardToggleOption[] {
        return this.types.map((type) => ({
            label: type.label,
            value: type.key,
            description: type.description,
            data: type,
        }));
    }

    protected selectType(index: number): void {
        this.activeTypeIndex = index;
    }

    protected selectTypeByValue(value: string): void {
        const index = this.types.findIndex((type) => type.key === value);
        if (index >= 0) {
            this.selectType(index);
        }
    }

    protected selectedNames(type: CerrixUserTypePickerConfig): string[] {
        return this.selectedUserList(type).map((user) => user.label);
    }

    protected selectedUserList(type: CerrixUserTypePickerConfig): CerrixSelectListOption[] {
        const selected = this.selectedValues[type.key] ?? [];
        return this.userOptions(type).filter((user) => selected.includes(user.value));
    }

    protected setSelectedUsers(type: CerrixUserTypePickerConfig, values: string[]): void {
        this.selectedValues = { ...this.selectedValues, [type.key]: values };
        this.value = this.selectedValues;
        this.valueChange.emit(this.value);
    }

    // app-cerrix-user-picker's non-multiselect mode binds a single `value`/`valueChange`, not
    // the `values`/`valuesChange` array pair, so single-select types adapt at this boundary.
    protected singleValueFor(type: CerrixUserTypePickerConfig): string | null {
        return this.selectedValues[type.key]?.[0] ?? null;
    }

    protected setSingleValue(type: CerrixUserTypePickerConfig, value: string | null): void {
        this.setSelectedUsers(type, value ? [value] : []);
    }

    private userOptions(type: CerrixUserTypePickerConfig): CerrixSelectListOption[] {
        return type.users.map((user) => ({
            label: user.Name,
            value: user.Guid,
            avatarName: user.Name,
        }));
    }
}
